import { baseApi } from '@/api/baseApi';
import type {
    ApproveProfessionalApplicationRequest,
    ApproveProfessionalApplicationResponse,
    GetProfessionalApplicationResponse,
    ListProfessionalApplicationsFilters,
    ListProfessionalApplicationsResponse,
    RejectProfessionalApplicationRequest,
    RejectProfessionalApplicationResponse
} from '@/lib/professionalApplicationsSchemas';
import {
    ApproveProfessionalApplicationResponseSchema,
    GetProfessionalApplicationResponseSchema,
    ListProfessionalApplicationsResponseSchema,
    RejectProfessionalApplicationResponseSchema
} from '@/lib/professionalApplicationsSchemas';
import { parseWithSchema } from '@/lib/safeParse';

/**
 * RTK Query endpoints для admin-backend /professional-applications/*.
 * admin-backend на порту 5002 без префикса /admin.
 *
 * Историческое название (до v0.10 / EPIC 1 фичи "Сотрудник + Кафе"):
 * chefApplicationsApi.
 */
export const professionalApplicationsApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        listProfessionalApplications: build.query<
            ListProfessionalApplicationsResponse,
            ListProfessionalApplicationsFilters | void
        >({
            query: (params) => {
                const search: Record<string, string> = {};
                if (params?.status) {
                    search.status = params.status;
                }
                if (params?.search !== undefined && params.search !== '') {
                    search.search = params.search;
                }
                if (params?.limit !== undefined) {
                    search.limit = String(params.limit);
                }
                if (params?.offset !== undefined) {
                    search.offset = String(params.offset);
                }
                return {
                    url: '/professional-applications',
                    method: 'GET',
                    params: search
                };
            },
            transformResponse: (raw: unknown): ListProfessionalApplicationsResponse =>
                parseWithSchema(ListProfessionalApplicationsResponseSchema, raw),
            providesTags: (result) =>
                result
                    ? [
                        ...result.items.map((a) => ({
                            type: 'ProfessionalApplication' as const,
                            id: a.id
                        })),
                        { type: 'ProfessionalApplication' as const, id: 'LIST' }
                    ]
                    : [{ type: 'ProfessionalApplication' as const, id: 'LIST' }]
        }),

        getProfessionalApplication: build.query<
            GetProfessionalApplicationResponse,
            { id: string }
        >({
            query: ({ id }) => ({
                url: `/professional-applications/${id}`,
                method: 'GET'
            }),
            transformResponse: (raw: unknown): GetProfessionalApplicationResponse =>
                parseWithSchema(GetProfessionalApplicationResponseSchema, raw),
            providesTags: (_result, _error, arg) => [
                { type: 'ProfessionalApplication' as const, id: arg.id }
            ]
        }),

        approveProfessionalApplication: build.mutation<
            ApproveProfessionalApplicationResponse,
            { id: string; body: ApproveProfessionalApplicationRequest }
        >({
            query: ({ id, body }) => ({
                url: `/professional-applications/${id}/approve`,
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): ApproveProfessionalApplicationResponse =>
                parseWithSchema(ApproveProfessionalApplicationResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'ProfessionalApplication', id: arg.id },
                { type: 'ProfessionalApplication', id: 'LIST' }
            ]
        }),

        rejectProfessionalApplication: build.mutation<
            RejectProfessionalApplicationResponse,
            { id: string; body: RejectProfessionalApplicationRequest }
        >({
            query: ({ id, body }) => ({
                url: `/professional-applications/${id}/reject`,
                method: 'POST',
                body
            }),
            transformResponse: (raw: unknown): RejectProfessionalApplicationResponse =>
                parseWithSchema(RejectProfessionalApplicationResponseSchema, raw),
            invalidatesTags: (_result, _error, arg) => [
                { type: 'ProfessionalApplication', id: arg.id },
                { type: 'ProfessionalApplication', id: 'LIST' }
            ]
        })
    })
});

export const {
    useListProfessionalApplicationsQuery,
    useGetProfessionalApplicationQuery,
    useApproveProfessionalApplicationMutation,
    useRejectProfessionalApplicationMutation
} = professionalApplicationsApi;
